import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import {AuthModal} from './auth-modal';

export type AuthModalMode = 'login' | 'register';

export interface AuthModalOptions {
  mode?: AuthModalMode;
  /** Small banner shown above heading, e.g. "We saved your prompt!" */
  subtitle?: string;
  /** The user's prompt snippet to display in the modal */
  savedPrompt?: string;
  /** Where to send the user after successful auth (defaults to getRedirectUri()) */
  redirectTo?: string;
}

interface AuthModalContextValue {
  open: (options?: AuthModalMode | AuthModalOptions) => void;
  close: () => void;
}

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

const DEFAULT_OPTIONS: Required<AuthModalOptions> = {
  mode: 'register',
  subtitle: '',
  savedPrompt: '',
  redirectTo: '',
};

export function AuthModalProvider({children}: {children: ReactNode}) {
  const [opts, setOpts] = useState<Required<AuthModalOptions>>(DEFAULT_OPTIONS);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback((input?: AuthModalMode | AuthModalOptions) => {
    if (!input || typeof input === 'string') {
      setOpts({...DEFAULT_OPTIONS, mode: input ?? 'register'});
    } else {
      setOpts({...DEFAULT_OPTIONS, ...input});
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <AuthModalContext.Provider value={{open, close}}>
      {children}
      {isOpen && (
        <AuthModal
          mode={opts.mode}
          subtitle={opts.subtitle}
          savedPrompt={opts.savedPrompt}
          redirectTo={opts.redirectTo || undefined}
          onClose={close}
        />
      )}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal(): AuthModalContextValue | null {
  return useContext(AuthModalContext);
}
